import React, { useEffect, useState } from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import { useForm, Controller } from 'react-hook-form';
import DatePicker from 'react-datepicker';

import Modal from '../../components/Modal';
import Button from '../../components/Button';
import { CadernoLPMAExtensaoMonitoramento } from '../../models/modelsCadernoLPMA';
import { STRINGS_CADERNOLPMA } from '../../util/cadernoLPMA/string';

interface ExtensaoMonitoramentoModalProps {
	show: boolean;
	onHide: () => void;
	onSubmit: (info: CadernoLPMAExtensaoMonitoramento) => void;
	dadosExtensaoMonitoramento?: CadernoLPMAExtensaoMonitoramento | null;
}

const defaultValues: CadernoLPMAExtensaoMonitoramento = {
	previsaoLPMA: '',
	dataInicioExtensaoMonitoramento: null,
	responsavelInicioExtensaoMonitoramento: '',
	dataTerminoExtensaoMonitoramento: null,
	responsavelTerminoExtensaoMonitoramento: '',
	comunicacaoExtensaoMonitoramentoJson: '',
	observacoesExtensaoMonitoramento: '',
};

const formatarDataParaJson = (valor: any) => {
	if (!valor) return null;
	const data = valor instanceof Date ? valor : new Date(valor);
	return Number.isNaN(data.getTime()) ? null : data.toISOString();
};

const formatarPrevisaoLpmaParaRadio = (valor: any): string => {
	if (valor === true || valor === 'true') return 'true';
	if (valor === false || valor === 'false') return 'false';
	return '';
};

const normalizarRadio = (valor: any): boolean | null => {
	if (valor === undefined || valor === null || valor === '') return null;
	if (typeof valor === 'boolean') return valor;
	return valor === 'true';
};

export default function ExtensaoMonitoramentoModal({ show, onHide, onSubmit, dadosExtensaoMonitoramento }: ExtensaoMonitoramentoModalProps) {
	const {
		register,
		handleSubmit,
		control,
		reset,
		setValue,
		getValues,
	} = useForm<CadernoLPMAExtensaoMonitoramento>({
		mode: 'all',
		defaultValues,
	});

	const [linhasComunicacao, setLinhasComunicacao] = useState<number>(1);

	useEffect(() => {
		if (!show) {
			reset(defaultValues);
			setLinhasComunicacao(1);
		} else if (dadosExtensaoMonitoramento) {
			const parseDate = (valor: any) => {
				if (!valor) return null;
				const data = new Date(valor);
				return Number.isNaN(data.getTime()) ? null : data;
			};

			// Parse do JSON de comunicação
			let comunicacoes: any[] = [];
			try {
				if (dadosExtensaoMonitoramento.comunicacaoExtensaoMonitoramentoJson) {
					comunicacoes = JSON.parse(dadosExtensaoMonitoramento.comunicacaoExtensaoMonitoramentoJson);
				}
			} catch (e) {}

			setLinhasComunicacao(Math.max(1, comunicacoes.length));

			const dadosParaReset: any = {
				...dadosExtensaoMonitoramento,
				previsaoLPMA: formatarPrevisaoLpmaParaRadio(dadosExtensaoMonitoramento.previsaoLPMA),
				dataInicioExtensaoMonitoramento: parseDate(dadosExtensaoMonitoramento.dataInicioExtensaoMonitoramento),
				dataTerminoExtensaoMonitoramento: parseDate(dadosExtensaoMonitoramento.dataTerminoExtensaoMonitoramento),
			};

			// Popular campos de comunicação
			comunicacoes.forEach((item: any, index: number) => {
				dadosParaReset[`comunicacaoData${index}`] = parseDate(item.data);
			});

			reset(dadosParaReset);
		}
	}, [show, dadosExtensaoMonitoramento, reset]);

	const adicionarLinhaComunicacao = () => setLinhasComunicacao((prev) => prev + 1);
	const removerLinhaComunicacao = (index: number) => {
		if (linhasComunicacao === 1) return;
		setLinhasComunicacao((prev) => prev - 1);
		setValue(`comunicacaoData${index}` as any, null);
	};

	const handleSubmitInterno = (dados: CadernoLPMAExtensaoMonitoramento) => {
		const valores = getValues() as Record<string, any>;
		const dadosNormalizados: Record<string, any> = { ...dados };

		const registrosComunicacao: any[] = [];
		for (let i = 0; i < linhasComunicacao; i++) {
			const data = valores[`comunicacaoData${i}`];
			if (data) {
				registrosComunicacao.push({
					data: formatarDataParaJson(data),
				});
			}
			delete dadosNormalizados[`comunicacaoData${i}`];
		}

		dadosNormalizados.comunicacaoExtensaoMonitoramentoJson = registrosComunicacao.length ? JSON.stringify(registrosComunicacao) : null;
		dadosNormalizados.previsaoLPMA = normalizarRadio(valores.previsaoLPMA ?? dados.previsaoLPMA);

		onSubmit(dadosNormalizados as CadernoLPMAExtensaoMonitoramento);
		onHide();
	};

	return (
		<Modal show={show} onHide={onHide} title={STRINGS_CADERNOLPMA.EXTENSAOMONITORAMENTO_MODAL_TITLE} bodyClassName="caderno-lpma-modal-body">
			<Form onSubmit={handleSubmit(handleSubmitInterno)} autoComplete="off">

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>
							<h5>Formulário de extensão do período de monitoramento</h5>
						</Form.Label>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>É previsto na LPMA / Parecer Técnico na íntegra a extensão do período de monitoramento?</Form.Label>
						<Controller
							control={control}
							name="previsaoLPMA"
							render={({ onChange, value }) => (
								<div className="d-flex">
									<div className="form-check mr-3">
										<input
											type="radio"
											className="form-check-input"
											id="previsaoLPMASim"
											checked={value === true || value === 'true'}
											onChange={() => onChange('true')}
										/>
										<label className="form-check-label" htmlFor="previsaoLPMASim">
											Sim
										</label>
									</div>
									<div className="form-check">
										<input
											type="radio"
											className="form-check-input"
											id="previsaoLPMANao"
											checked={value === false || value === 'false'}
											onChange={() => onChange('false')}
										/>
										<label className="form-check-label" htmlFor="previsaoLPMANao">
											Não
										</label>
									</div>
								</div>
							)}
						/>
						<p className="mt-2">
							<small>
								* Em caso negativo entrar em contato com a CIBio através do e-mail cibio@basf.com
							</small>
						</p>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Comunicação à CIBio sobre a extensão do período de monitoramento – Data(s):</Form.Label>
					</Form.Group>
				</Form.Row>

				<Row className="row-custom font-weight-bold">
					<Col md={3}>Data</Col>
				</Row>

				{Array.from({ length: linhasComunicacao }).map((_, index) => (
					<Row className="row-custom align-items-end" key={`comunicacao-${index}`}>
						<Col md={3}>
							<Controller
								control={control}
								name={`comunicacaoData${index}` as any}
								render={({ onChange, value }) => (
									<DatePicker
										selected={value || null}
										onChange={onChange}
										className="input-custom-to-date"
										locale="pt-BR"
										dateFormat="dd/MM/yyyy"
										placeholderText="Selecionar a data"
									/>
								)}
							/>
						</Col>
						<Col md="auto" className="d-flex justify-content-end">
							{linhasComunicacao > 1 && (
								<Button title="Remover" contained={false} type="button" onClick={() => removerLinhaComunicacao(index)} />
							)}
						</Col>
					</Row>
				))}

				<div className="button-position" style={{ justifyContent: 'flex-start' }}>
					<Button title="Adicionar data" contained={false} type="button" onClick={adicionarLinhaComunicacao} />
				</div>

				<p className="mt-2">
					<small>
						IMPORTANTE: Solicitar a autorização da CIBio antes do término do período de monitoramento.
						As autorizações emitidas deverão ser anexadas na pasta de campo - ABA COMUNICAÇÃO INTERNA.
					</small>
				</p>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Data inicial da extensão / monitoramento</Form.Label>
						<Row>
							<Col md={6}>
								<Controller
									control={control}
									name="dataInicioExtensaoMonitoramento"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Col>
							<Col md={6}>
								<Form.Control
									ref={register}
									name="responsavelInicioExtensaoMonitoramento"
									placeholder="Responsável"
									maxLength={150}
								/>
							</Col>
						</Row>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Data final da extensão / monitoramento</Form.Label>
						<Row>
							<Col md={6}>
								<Controller
									control={control}
									name="dataTerminoExtensaoMonitoramento"
									render={({ onChange, value }) => (
										<DatePicker
											selected={value || null}
											onChange={onChange}
											className="input-custom-to-date"
											locale="pt-BR"
											dateFormat="dd/MM/yyyy"
											placeholderText="Selecionar a data"
										/>
									)}
								/>
							</Col>
							<Col md={6}>
								<Form.Control
									ref={register}
									name="responsavelTerminoExtensaoMonitoramento"
									placeholder="Responsável"
									maxLength={150}
								/>
							</Col>
						</Row>
					</Form.Group>
				</Form.Row>

				<Form.Row className="row-custom">
					<Form.Group as={Col} md={12}>
						<Form.Label>Observações e informações adicionais</Form.Label>
						<Form.Control
							as="textarea"
							rows={3}
							ref={register}
							name="observacoesExtensaoMonitoramento"
							placeholder="Registre observações adicionais"
							maxLength={500}
						/>
					</Form.Group>
				</Form.Row>

				<div className="button-position">
					<Button title="Cancelar" contained={false} type="button" onClick={onHide} />
					<Button title="Salvar" contained={true} type="submit" />
				</div>
			</Form>
		</Modal>
	);
}

